import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import api, { setAccessToken, registerSessionExpiredHandler } from '../lib/api';
import wsService from '../services/websocket';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Internal PTC roles (PTC staff - NOT Prestigious Travel Club)
const INTERNAL_ROLES = ['ptc_admin', 'ptc_director', 'ptc_manager', 'ptc_team_member', 'ptc_principal_1'];
// PTC roles that can access EasyQuickFund application processing
const PTC_PROCESSING_ROLES = ['ptc_admin', 'ptc_director', 'ptc_manager', 'ptc_team_member', 'ptc_principal_1'];
// Admin-level roles that can manage users/companies (global)
const ADMIN_ROLES = ['ptc_admin'];
// Manager-level roles (can reassign clients)
const MANAGER_ROLES = ['ptc_admin', 'ptc_director', 'ptc_manager'];
// Roles that can bulk delete records (Admin + Director)
const BULK_DELETE_ROLES = ['ptc_admin', 'ptc_director'];
// Roles that can manage users within their own company
const USER_MANAGEMENT_ROLES = ['ptc_admin', 'company_director'];

// v3.21-1799 · Financial-goal edit tier — MIRRORS the backend
// `require_admin` gate at services/deal_permissions.assert_is_admin
// (which internally checks `is_director` against
// services.auth_roles.DIRECTOR_TIER_EXTENDED_ROLES).  This is the
// EXACT effective authorization used by `PUT /api/monthly-goal`.
// Keep this set in lock-step with the backend frozenset; do NOT
// narrow it to `ptc_admin` (isAdmin) and do NOT broaden it to
// `ptc_manager` (isManager).  A dedicated helper is used so the UI
// pencil/Set-Goal affordance appears IFF the backend PUT would
// accept the same caller.
const FINANCIAL_GOAL_EDIT_ROLES = [
  'ptc_admin',
  'ptc_director',
  'director',
  'manager_director',
  'admin',
  'super_admin',
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // v3.15-578: Read token from sessionStorage (XSS-safe, tab-scoped)
  const initialToken = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
  // Migrate any lingering localStorage token to sessionStorage
  if (!sessionStorage.getItem('access_token') && localStorage.getItem('access_token')) {
    sessionStorage.setItem('access_token', localStorage.getItem('access_token'));
    localStorage.removeItem('access_token');
  }
  // v3.21-1729 · CRITICAL: seed the in-memory access token used by the
  // axios interceptor BEFORE any API call fires.  Without this, the
  // interceptor's refresh path stayed disabled on page reload and every
  // authenticated request 401'd with a raw "Invalid or expired token"
  // once the access-token TTL elapsed.
  if (initialToken) setAccessToken(initialToken);
  console.log('[AuthContext v3.21-1729] Initial mount, token:', initialToken ? 'EXISTS' : 'NULL');
  
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const fetchAttempted = useRef(false);
  
  const [featureFlags, setFeatureFlags] = useState({
    lending_enabled: false,
    lending_enabled_for_partners: false,
    company_lending_enabled: false,
    company_settlement_enabled: false,
    company_advanced_reporting: false,
    company_plan_type: 'Standard',
  });

  // v3.14-172: View As User — admin session simulation
  const [viewAsUser, setViewAsUser] = useState(null);
  const effectiveUser = viewAsUser || user;
  const isViewingAsUser = !!viewAsUser;
  const exitViewAs = () => setViewAsUser(null);

  // v3.21-1800 · PWA SILENT-REFRESH-ON-STARTUP.
  //
  //   ROOT CAUSE OF REPEATED PWA LOGIN (owner report):
  //     The 15-minute JWT access token lives in `sessionStorage` which
  //     is wiped whenever the standalone PWA is fully closed.  The
  //     durable 30-day `hub_refresh` HttpOnly cookie is intact across
  //     that close.  fetchUser (below) short-circuits to the login
  //     screen the moment `sessionStorage.access_token` is null — it
  //     NEVER tries `/api/auth/session/refresh`, so the cookie is
  //     wasted and the operator is asked to sign in again every launch.
  //
  //   FIX (this helper):
  //     Fire ONE POST /api/auth/session/refresh at boot if there is no
  //     local access token, we are not on the login page, and the
  //     previous session did not end with an explicit logout.  If the
  //     server rotates a fresh refresh token and returns 200 with an
  //     access_token, seed sessionStorage + in-memory so the standard
  //     fetchUser path succeeds without any interactive prompt.  Any
  //     401/403 (revoked / expired / suspended) falls through to the
  //     ordinary login screen.
  //
  //     Explicit logout writes `hub_explicit_logout=1` before clearing
  //     state so the very next boot deliberately skips this restore
  //     and the operator must sign in again.  The flag is one-shot —
  //     the helper clears it after a single evaluation.
  //
  //   GUARANTEES:
  //     · Runs at most once per mount (`startupRestoreRef`).
  //     · Never called from the /login screen (no thrash on the
  //       login form).
  //     · Never called when a token is already present.
  //     · Never called after an explicit logout.
  //     · Never touched by the axios interceptor's own refresh path
  //       (that continues to handle in-flight 401s independently and
  //       uses the same single-flight queue in `lib/api.js`).
  const startupRestoreRef = useRef(false);
  const attemptStartupSessionRestore = useCallback(async () => {
    if (startupRestoreRef.current) return false;
    startupRestoreRef.current = true;
    if (sessionStorage.getItem('access_token')) return false;
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/login')) {
      return false;
    }
    // Consume one-shot explicit-logout flag — respect the operator's
    // intent to be signed out on this device.
    const explicit = sessionStorage.getItem('hub_explicit_logout')
                  || localStorage.getItem('hub_explicit_logout');
    if (explicit) {
      try {
        sessionStorage.removeItem('hub_explicit_logout');
        localStorage.removeItem('hub_explicit_logout');
      } catch (_e) { /* noop */ }
      return false;
    }
    try {
      // Bypass the axios instance so the interceptor never observes a
      // 401 from a bare `/auth/me` call before we know whether the
      // refresh cookie is valid.
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(
        `${backendUrl}/api/auth/session/refresh`,
        {},
        { withCredentials: true, timeout: 12000 },
      );
      const newTok = res?.data?.access_token || null;
      if (!newTok) return false;
      setAccessToken(newTok);
      sessionStorage.setItem('access_token', newTok);
      console.log('[AuthContext v3.21-1800] Silent session restore OK');
      return true;
    } catch (err) {
      // 401 / 403 / no cookie / expired — proceed to normal login flow.
      console.log('[AuthContext v3.21-1800] Silent session restore skipped:',
                  err?.response?.status || err?.message || 'no cookie');
      return false;
    }
  }, []);

  // v3.14-121: Fetch user data with RETRY logic
  const fetchUser = useCallback(async (retryCount = 0) => {
    // Prevent duplicate fetch attempts
    if (fetchAttempted.current && retryCount === 0) {
      console.log('[AuthContext v3.14-121] fetchUser already attempted, skipping');
      return;
    }

    // v3.21-1800 · Before giving up because sessionStorage is empty,
    // give the durable `hub_refresh` cookie a chance to restore the
    // session silently.  On success we fall through to the ordinary
    // `/auth/me` flow with a freshly minted access token.
    if (!sessionStorage.getItem('access_token') && retryCount === 0) {
      await attemptStartupSessionRestore();
    }

    const currentToken = sessionStorage.getItem('access_token');
    console.log('[AuthContext v3.15-578] fetchUser called, token:', currentToken ? 'EXISTS' : 'NULL', 'retry:', retryCount);
    
    if (!currentToken) {
      console.log('[AuthContext v3.14-121] No token, setting loading=false');
      setLoading(false);
      setAuthChecked(true);
      return;
    }
    
    fetchAttempted.current = true;
    
    try {
      console.log('[AuthContext v3.14-121] Calling /auth/me...');
      const response = await api.get('/auth/me');
      console.log('[AuthContext v3.14-121] /auth/me SUCCESS, user:', response.data?.email);
      setUser(response.data);
      setToken(currentToken);
      if (response.data?.must_change_password) {
        setMustChangePassword(true);
      }
      
      // Connect to WebSocket for real-time messaging
      wsService.connect(currentToken);
      
      // Fetch feature flags (non-critical)
      try {
        const flagsResponse = await api.get('/features');
        setFeatureFlags(flagsResponse.data);
      } catch (flagError) {
        console.warn('[AuthContext v3.14-121] Feature flags fetch failed (non-critical)');
      }
    } catch (error) {
      console.error('[AuthContext v3.14-121] fetchUser error:', error?.response?.status, error?.message);
      
      // v3.14-121: RETRY once on any error before giving up
      if (retryCount < 1) {
        console.log('[AuthContext v3.14-121] Retrying fetchUser in 500ms...');
        fetchAttempted.current = false;
        await new Promise(resolve => setTimeout(resolve, 500));
        return fetchUser(retryCount + 1);
      }
      
      // v3.14-121: ONLY clear token on explicit 401 after retry
      if (error?.response?.status === 401) {
        console.log('[AuthContext v3.14-121] 401 after retry, clearing token');
        localStorage.removeItem('access_token');
        setToken(null);
        setUser(null);
      } else {
        // For other errors (network, 500, etc.), KEEP the token
        console.log('[AuthContext v3.14-121] Non-401 error after retry, preserving token and user state');
        // Keep whatever user data we have
      }
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, []);

  // v3.14-120: Single fetch on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // v3.21-1729 · One clean session-expired UX when the axios interceptor
  // exhausts refresh.  Preserves the tab's in-flight form data (React
  // component state stays mounted; only the auth surface changes).
  useEffect(() => {
    registerSessionExpiredHandler(() => {
      try { setAccessToken(null); } catch (_e) { /* noop */ }
      sessionStorage.removeItem('access_token');
      localStorage.removeItem('access_token');
      setToken(null);
      setUser(null);
      setAuthChecked(true);
      toast.error('Your session has expired. Please sign in again.');
      // Route to /login without wiping form state that other components
      // may still be holding; the user can copy content out if needed.
      if (!window.location.pathname.startsWith('/login')) {
        setTimeout(() => { window.location.assign('/login'); }, 400);
      }
    });
  }, []);

  // v3.14-120: Login with HARD token persistence
  const login = async (email, password) => {
    console.log('[AuthContext v3.14-120] Login attempt for:', email);
    
    const response = await api.post('/auth/session/login', { email, password, device_id: (window.__hubDeviceId || (window.__hubDeviceId = 'web-' + Math.random().toString(36).slice(2, 10))) });
    const { access_token: newToken, user: userData, must_change_password } = response.data;

    // v3.21-1684 · Access token lives in memory (api.js) + short-term
    // sessionStorage for in-tab reload compatibility.  The durable
    // credential is the HttpOnly `hub_refresh` cookie.
    setAccessToken(newToken);
    sessionStorage.setItem('access_token', newToken);
    localStorage.removeItem('access_token');   // remove legacy long-lived storage
    const verifyToken = sessionStorage.getItem('access_token');
    console.log('[AuthContext v3.21-1684] TOKEN SAVED:', verifyToken ? 'SUCCESS' : 'FAILED');
    
    if (!verifyToken) {
      throw new Error('Token storage failed');
    }
    
    // Update React state AFTER localStorage is confirmed
    setToken(newToken);
    setUser(userData);
    setAuthChecked(true);
    setMustChangePassword(!!must_change_password);
    fetchAttempted.current = true; // Prevent re-fetch
    
    // Connect to WebSocket
    wsService.connect(newToken);
    
    console.log('[AuthContext v3.15-578] Login complete, user:', userData?.email);
    return { token: newToken, user: userData };
  };

  // v3.21-1684 · Explicit logout — calls the server-side session revocation
  // endpoint so the HttpOnly refresh cookie AND the `refresh_sessions`
  // record are invalidated.  Push-subscription mapping for this device is
  // cleaned up backend-side (see routes/auth_session.py::session_logout).
  const logout = async () => {
    console.log('[AuthContext v3.21-1684] LOGOUT called explicitly');
    // v3.21-1800 · Set the one-shot explicit-logout flag BEFORE clearing
    // any state so the next PWA open deliberately skips the silent
    // session-restore path in `attemptStartupSessionRestore` and shows
    // the login screen.  The flag is consumed and cleared on that next
    // boot's very first evaluation.
    try {
      sessionStorage.setItem('hub_explicit_logout', '1');
      localStorage.setItem('hub_explicit_logout', '1');
    } catch (_e) { /* noop */ }
    try {
      await api.post('/auth/session/logout', {});
    } catch (e) {
      console.warn('[AuthContext v3.21-1684] server logout failed (proceeding client-side):', e?.message);
    }
    wsService.disconnect();
    setAccessToken(null);
    sessionStorage.removeItem('access_token');
    localStorage.removeItem('access_token'); // Clean up any legacy
    sessionStorage.removeItem('ghost_briefing_shown');
    setToken(null);
    setUser(null);
    setAuthChecked(true);
    fetchAttempted.current = false;
  };

  // Check if user is PTC Admin (always checks REAL user for admin gates like View As)
  const isAdmin = () => ADMIN_ROLES.includes(effectiveUser?.role);
  const isRealAdmin = () => ADMIN_ROLES.includes(user?.role);
  
  // Check if user can manage/reassign (PTC Admin, Director, Manager)
  const isManager = () => MANAGER_ROLES.includes(effectiveUser?.role);
  
  // Check if user is a team member (lowest level)
  const isTeamMember = () => effectiveUser?.role === 'ptc_team_member' || effectiveUser?.role === 'company_team_member';
  
  // Check if user belongs to internal company (PTC/EQF)
  const isInternal = () => effectiveUser?.company_type === 'internal' || INTERNAL_ROLES.includes(effectiveUser?.role);
  
  // Check if user is a PTC staff member (can process EasyQuickFund applications)
  const isPtcUser = () => PTC_PROCESSING_ROLES.includes(effectiveUser?.role);
  
  // Check if user belongs to external partner company
  const isExternal = () => effectiveUser?.company_type === 'external' || !isInternal();

  // v3.15-1002: VO officer detection — VO has its own scoped worldview and
  // is treated like an external user for navigation/route purposes (no
  // Dashboard / Calendar / Availability access).
  const isVoOfficer = () => effectiveUser?.role === 'vo_officer' || effectiveUser?.is_vo === true;

  // v3.15-1002: Single source of truth for "is this user allowed in the
  // internal workspace surface (Dashboard / Calendar / Availability)?".
  // Hard NO for external partners and VO officers; everything else internal.
  const canAccessWorkspace = () => isInternal() && !isVoOfficer();

  // v3.15-1096 — Company-style managers (external + non-VO) get
  // Dashboard access scoped to their own company. Lauren Chase's
  // actual role in the DB is `manager_director` (external), so we
  // accept any of the manager-class roles here.
  const COMPANY_MANAGER_ROLES = [
    'company_manager',
    'company_director',
    'company_admin',
    'manager_director',
    'manager_1',
    'md_team_member',
  ];
  const isCompanyManager = () => COMPANY_MANAGER_ROLES.includes(effectiveUser?.role);
  const canAccessDashboard = () => canAccessWorkspace() || isCompanyManager();

  // v3.18-1388 CRM-INT-005 — The main Dashboard (Home landing) is restricted
  // to an explicit user-ID allow-list.  Currently only the CEO (Al Davis)
  // may see or open it.  The backend enforces an identical gate at
  // /api/dashboard/*; this is the UI counterpart used to hide the sidebar
  // link and 404 the route for unauthorized users.
  const MAIN_DASHBOARD_ALLOWLIST = [
    'de1e1529-ea24-4cec-84d3-120e5b0c7294', // CEO — Al Davis
  ];
  const canViewMainDashboard = () =>
    !!effectiveUser?.id && MAIN_DASHBOARD_ALLOWLIST.includes(effectiveUser.id);
  
  // Check if user can manage users (PTC Admin globally, Company Director for their company)
  const canManageUsers = () => USER_MANAGEMENT_ROLES.includes(effectiveUser?.role);
  
  // Check if user can bulk delete records (Admin + Director)
  const canBulkDelete = () => BULK_DELETE_ROLES.includes(effectiveUser?.role);

  // v3.21-1799 · Frontend mirror of backend `require_admin`
  // (services/deal_permissions.assert_is_admin →
  //  services.auth_roles.DIRECTOR_TIER_EXTENDED_ROLES).  Used by the
  // Clients-page Monthly Goal card to decide whether to render the
  // edit pencil / Set Goal affordance.  Backend PUT remains
  // authoritative — this helper only gates UI visibility so
  // unauthorized users never see an edit control that would 403.
  const canEditFinancialGoal = () =>
    FINANCIAL_GOAL_EDIT_ROLES.includes(effectiveUser?.role);
  
  // Check if user is an external company director
  const isCompanyDirector = () => effectiveUser?.role === 'company_director';
  
  const isMarketingPartner = () => effectiveUser?.role === 'marketing_partner';

  // v3.15-1074: Can this user send Transactional SMS (Credit App / ACH links)?
  //
  //   Allowed companies:
  //     - Any internal company (company_type='internal'), OR
  //     - The "Prestigious Travel Club Inc" external subsidiary
  //       (sole approved external exception; case-insensitive name match).
  //
  //   AND one of:
  //     - role ∈ {ptc_admin, ptc_director, ptc_manager}, OR
  //     - is_application_concierge === true (explicit per-user grant).
  //
  // The backend enforces an IDENTICAL gate at the route layer — this
  // is purely a visibility hint for the UI.
  const _normalizeName = (n) => String(n || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const canSendSms = () => {
    const u = effectiveUser;
    if (!u) return false;
    const isInternalCo = (u.company_type === 'internal');
    const isPtcInc     = (_normalizeName(u.company_name) === 'prestigioustravelclubinc');
    if (!isInternalCo && !isPtcInc) return false;
    const role = u.role;
    if (['ptc_admin', 'ptc_director', 'ptc_manager'].includes(role)) return true;
    if (u.is_application_concierge === true) return true;
    return false;
  };

  const canAccessLending = () => featureFlags.lending_enabled;

  // v3.14-120: isAuthenticated helper - TRUE if token exists
  const isAuthenticated = () => {
    const hasToken = !!token || !!sessionStorage.getItem('access_token');
    return hasToken;
  };

  const value = {
    user: effectiveUser,
    realUser: user,
    token,
    loading,
    authChecked,
    login,
    logout,
    isAdmin,
    isRealAdmin,
    isManager,
    isTeamMember,
    isInternal,
    isPtcUser,
    isExternal,
    isVoOfficer,
    canAccessWorkspace,
    canAccessDashboard,
    canViewMainDashboard,
    isCompanyManager,
    canManageUsers,
    canBulkDelete,
    canEditFinancialGoal,
    isCompanyDirector,
    canAccessLending,
    isAuthenticated,
    isMarketingPartner,
    canSendSms,
    featureFlags,
    viewAsUser,
    setViewAsUser,
    isViewingAsUser,
    exitViewAs,
    mustChangePassword,
    clearMustChangePassword: () => setMustChangePassword(false),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
